"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Move, RotateCcw, Maximize2, Save, Trash2, Layers, X } from "lucide-react"
import { Button } from "./ui/button"

type PartInfo = {
  name: string
  path: string
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number; z: number }
}

type TransformMode = "translate" | "rotate" | "scale"

const ModularState = () => {
  const [parts, setParts] = useState<PartInfo[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [mode, setMode] = useState<TransformMode>("translate")
  const [refreshKey, setRefreshKey] = useState(0)

  const loadParts = useCallback(() => {
    const data = (window as any).getModularSetupParts?.()
    if (data) {
      setParts(data)
    }
  }, [])

  useEffect(() => {
    loadParts()
    ;(window as any).onModularTransformChange = () => {
      setRefreshKey((k) => k + 1)
    }
    return () => {
      ;(window as any).onModularTransformChange = null
    }
  }, [loadParts])

  useEffect(() => {
    loadParts()
  }, [refreshKey, loadParts])

  const handleSelect = (idx: number) => {
    setSelectedIndex(idx)
    ;(window as any).selectModularPart?.(idx)
    ;(window as any).setModularTransformMode?.(mode)
  }

  const handleDeselect = () => {
    setSelectedIndex(null)
    ;(window as any).deselectModularPart?.()
  }

  const handleModeChange = (newMode: TransformMode) => {
    setMode(newMode)
    ;(window as any).setModularTransformMode?.(newMode)
  }

  const handleSave = async () => {
    await (window as any).save_modular_transforms?.()
  }

  if (parts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
        <Layers className="h-10 w-10 text-primary/30" />
        <p className="text-sm text-primary/60 text-center leading-relaxed">
          Nenhum setup modular carregado.
        </p>
        <p className="text-xs text-primary/40 text-center leading-relaxed">
          Abra um setup modular para editar as peças individualmente.
        </p>
      </div>
    )
  }

  const current = selectedIndex !== null ? parts[selectedIndex] : null

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-4 w-4 text-purple-400" />
          <span className="text-xs font-semibold text-primary">Peças do Setup</span>
        </div>

        {current && (
          <div className="flex gap-1">
            {([
              { key: "translate" as const, icon: Move, label: "Mover" },
              { key: "rotate" as const, icon: RotateCcw, label: "Girar" },
              { key: "scale" as const, icon: Maximize2, label: "Escala" },
            ]).map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => handleModeChange(key)}
                className={`flex-1 flex items-center justify-center gap-1 rounded px-2 py-1.5 text-[10px] font-medium transition-colors ${
                  mode === key
                    ? "bg-purple-600 text-white"
                    : "border border-white/10 text-primary/50 hover:text-primary hover:border-white/25"
                }`}
                title={label}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {parts.map((part, idx) => (
          <div
            key={idx}
            onClick={() => handleSelect(idx)}
            className={`rounded-lg px-3 py-2 cursor-pointer transition-all ${
              selectedIndex === idx
                ? "bg-purple-600/20 border border-purple-500/40"
                : "border border-white/10 hover:border-white/25 hover:bg-white/5"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs font-medium text-primary truncate">
                  {part.name || `Peça ${idx + 1}`}
                </p>
                {selectedIndex === idx && (
                  <div className="mt-1.5 space-y-1">
                    {(["position", "rotation", "scale"] as const).map((prop) => (
                      <div key={prop} className="flex items-center gap-1">
                        <span className="text-[9px] text-primary/40 w-7 flex-shrink-0">
                          {prop === "position" ? "Pos" : prop === "rotation" ? "Rot" : "Scl"}
                        </span>
                        {(["x", "y", "z"] as const).map((axis) => (
                          <input
                            key={axis}
                            type="number"
                            step={prop === "scale" ? "0.001" : "0.01"}
                            value={parseFloat((part[prop] as any)[axis].toFixed(3))}
                            onChange={(e) => {
                              ;(window as any).updateModularPartTransform?.(
                                idx,
                                prop,
                                axis,
                                e.target.value
                              )
                              setParts((prev) => {
                                const next = [...prev]
                                next[idx] = {
                                  ...next[idx],
                                  [prop]: {
                                    ...next[idx][prop],
                                    [axis]: parseFloat(e.target.value) || 0,
                                  },
                                }
                                return next
                              })
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 min-w-0 rounded border border-white/10 bg-background px-1 py-0.5 text-[10px] text-primary focus:border-purple-500 focus:outline-none text-center"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedIndex === idx && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeselect()
                  }}
                  className="ml-2 p-1 text-primary/40 hover:text-red-400 transition-colors flex-shrink-0"
                  title="Desselecionar"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-3 py-2 border-t border-white/10">
        <Button
          onClick={handleSave}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs h-8"
        >
          <Save className="h-3.5 w-3.5 mr-1.5" />
          Salvar Transforms
        </Button>
      </div>
    </div>
  )
}

export default ModularState
